import { defineStore, acceptHMRUpdate } from 'pinia'
import { authService } from '~/services/api/auth'
import type { LoginCredentials, AuthUser, TokenPayload, LoginResult } from '~/types/auth'

interface AuthState {
  token: string | null
  user: AuthUser | null
  error: string | null
  isLoading: boolean
  refreshPromise: Promise<void> | null
  isRemembered: boolean
  // Holds the JWT returned by `/login` when the backend signals
  // `status: "otp_required"`. It is NOT the authoritative session token
  // (so `isAuthenticated` stays false) and is consumed by `verifyOtp`.
  pendingOtpToken: string | null
  pendingOtpRemembered: boolean
}

interface StoredAuthData {
  token: string
  expiresAt: number
  isRemembered: boolean
}

const TOKEN_KEY = 'auth:token'
const OTP_PENDING_KEY = 'auth:otp_pending'

interface StoredPendingOtp {
  token: string
  isRemembered: boolean
}

const getStoragePendingOtp = (): StoredPendingOtp | null => {
  if (process.server) return null
  try {
    const raw = sessionStorage.getItem(OTP_PENDING_KEY)
    return raw ? (JSON.parse(raw) as StoredPendingOtp) : null
  } catch {
    return null
  }
}

const setStoragePendingOtp = (data: StoredPendingOtp | null): void => {
  if (process.server) return
  try {
    if (!data) {
      sessionStorage.removeItem(OTP_PENDING_KEY)
      return
    }
    sessionStorage.setItem(OTP_PENDING_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('❌ Error storing pending OTP token:', error)
  }
}

// SSR-safe storage access
const getStorageToken = (): StoredAuthData | null => {
  if (process.server) {
    console.log('🖥️ Server-side: skipping token retrieval')
    return null
  }
  
  try {
    console.log('🔍 Attempting to retrieve token from storage...')
    
    // Try sessionStorage first
    let data = sessionStorage.getItem(TOKEN_KEY)
    console.log('📊 SessionStorage check:', data ? 'found' : 'not found')
    
    if (!data) {
      // If not in session, try localStorage
      data = localStorage.getItem(TOKEN_KEY)
      console.log('📊 LocalStorage check:', data ? 'found' : 'not found')
    }
    
    if (!data) {
      console.log('❌ No token found in any storage')
      return null
    }

    console.log('📦 Retrieved stored token data')
    const parsed = JSON.parse(data) as StoredAuthData
    
    // Check if token has expired
    if (parsed.expiresAt < Date.now()) {
      console.log('⌛ Token expired in storage, clearing...')
      // Clear expired token
      sessionStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(TOKEN_KEY)
      return null
    }
    
    console.log('✅ Valid token found in storage, expires:', new Date(parsed.expiresAt).toISOString())
    return parsed
  } catch (error) {
    console.error('❌ Error parsing stored token:', error)
    return null
  }
}

const setStorageToken = (data: StoredAuthData | null): void => {
  if (process.server) return
  try {
    if (!data) {
      console.log('🗑️ Clearing token from storage')
      sessionStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(TOKEN_KEY)
      return
    }

    console.log('💾 Storing token data:', { 
      isRemembered: data.isRemembered,
      expiresAt: new Date(data.expiresAt).toISOString()
    })

    const storage = data.isRemembered ? localStorage : sessionStorage
    storage.setItem(TOKEN_KEY, JSON.stringify(data))
    
    // Clear from the other storage to prevent duplicates
    const otherStorage = data.isRemembered ? sessionStorage : localStorage
    otherStorage.removeItem(TOKEN_KEY)
  } catch (error) {
    console.error('❌ Error storing token:', error)
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    user: null,
    error: null,
    isLoading: false,
    refreshPromise: null,
    isRemembered: false,
    pendingOtpToken: null,
    pendingOtpRemembered: false
  }),

  getters: {
    isAuthenticated: (state: AuthState) => !!state.token,
    otpRequired: (state: AuthState) => !!state.pendingOtpToken,
    isAdmin: (state: AuthState) => state.user?.role === 'admin',
    // Admins can access the owners console too.
    isOwner: (state: AuthState) => state.user?.role === 'owner' || state.user?.role === 'admin',
    // Role-aware destination for the generic "Dashboard" link.
    // Add new role branches here as we introduce more consoles.
    dashboardPath: (state: AuthState): string => {
      switch (state.user?.role) {
        case 'admin':
          return '/admin'
        case 'owner':
          // Owner dashboard is hidden until built out — land on reservations.
          return '/owner/reservations'
        default:
          return '/dashboard'
      }
    },
    // i18n key for the dashboard link label that matches `dashboardPath`.
    dashboardLabelKey: (state: AuthState): string => {
      switch (state.user?.role) {
        case 'admin':
          return 'nav.admin_dashboard'
        case 'owner':
          return 'nav.owner_dashboard'
        default:
          return 'nav.dashboard'
      }
    },
    tokenData: (state: AuthState): TokenPayload | null =>
      state.token ? authService.parseToken(state.token) : null
  },

  actions: {
    async init() {
      console.log('🚀 Initializing auth store')
      if (process.server) {
        console.log('⏭️ Skipping init on server-side')
        return
      }
      
      const storedData = getStorageToken()
      console.log('📥 Init retrieved stored data:', storedData ? 'found' : 'not found')
      
      if (storedData) {
        console.log('🔄 Setting token from storage')
        this.token = storedData.token
        this.isRemembered = storedData.isRemembered
        await this.checkAuth()
      } else {
        console.log('⚠️ No valid stored token found during init')
      }

      // Restore a pending OTP token (e.g. when the user reloads the
      // verify-otp page) so the OTP flow can resume without re-login.
      if (!this.pendingOtpToken) {
        const pending = getStoragePendingOtp()
        if (pending) {
          this.pendingOtpToken = pending.token
          this.pendingOtpRemembered = pending.isRemembered
        }
      }
    },

    async login(credentials: LoginCredentials): Promise<LoginResult> {
      this.isLoading = true
      this.error = null
      try {
        const token = await authService.login(
          credentials.username,
          credentials.password,
          credentials.rememberMe
        )

        // Inspect the JWT to detect whether the backend is requesting an OTP
        // step before granting full access.
        let payload: TokenPayload | null = null
        try {
          payload = authService.parseToken(token)
        } catch {
          payload = null
        }

        if (payload?.status === 'otp_required') {
          console.log('🔐 OTP required for this login — deferring full session')
          this.pendingOtpToken = token
          this.pendingOtpRemembered = credentials.rememberMe
          setStoragePendingOtp({
            token,
            isRemembered: credentials.rememberMe
          })
          return { success: true, otpRequired: true }
        }

        // Calculate token expiration
        const expiresIn = credentials.rememberMe ? 30 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000 // 30 days or 24 hours
        const expiresAt = Date.now() + expiresIn

        // Store token with metadata
        this.setToken(token, expiresAt, credentials.rememberMe)
        
        // Fetch complete user data from /me endpoint
        const fullUserData = await authService.me(token)
        this.user = fullUserData
        
        this.isRemembered = credentials.rememberMe
        return { success: true, otpRequired: false }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Login failed'
        this.error = message
        return { success: false, error: message }
      } finally {
        this.isLoading = false
      }
    },

    async verifyOtp(otp: string): Promise<LoginResult> {
      if (!this.pendingOtpToken) {
        const message = 'No pending OTP session — please log in again'
        this.error = message
        return { success: false, error: message }
      }

      this.isLoading = true
      this.error = null
      try {
        const { token: rotatedToken } = await authService.verifyOtp(
          this.pendingOtpToken,
          otp
        )

        // OTP cleared — promote rotated token to the authoritative session.
        const isRemembered = this.pendingOtpRemembered
        const expiresIn = isRemembered
          ? 30 * 24 * 60 * 60 * 1000
          : 24 * 60 * 60 * 1000
        const expiresAt = Date.now() + expiresIn

        this.setToken(rotatedToken, expiresAt, isRemembered)
        this.isRemembered = isRemembered
        this.pendingOtpToken = null
        this.pendingOtpRemembered = false
        setStoragePendingOtp(null)

        const fullUserData = await authService.me(rotatedToken)
        this.user = fullUserData

        return { success: true, otpRequired: false }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'OTP verification failed'
        this.error = message
        return { success: false, error: message }
      } finally {
        this.isLoading = false
      }
    },

    cancelOtp() {
      console.log('↩️ Cancelling pending OTP flow')
      this.pendingOtpToken = null
      this.pendingOtpRemembered = false
      this.error = null
      setStoragePendingOtp(null)
    },

    async checkAuth() {
      // A full page reload (F5) resets the Pinia store, but the token may
      // still be in storage. Rehydrate it here so route middleware that calls
      // checkAuth() before the store is initialized doesn't treat the user as
      // logged out and bounce them away from the page they reloaded.
      if (!this.token) {
        const stored = getStorageToken()
        if (stored) {
          this.token = stored.token
          this.isRemembered = stored.isRemembered
        }
      }

      if (!this.token) return false
      
      // Check if token is expired
      const tokenData = this.tokenData
      if (tokenData && tokenData.exp) {
        const now = Math.floor(Date.now() / 1000)
        const timeUntilExpiry = tokenData.exp - now
        
        // If token expires in less than 5 minutes, try to refresh it
        if (timeUntilExpiry < 300) {
          await this.refreshToken()
        }
      }
      
      try {
        const response = await authService.me(this.token)
        this.user = response
        return true
      } catch {
        this.clearToken()
        this.user = null
        return false
      }
    },

    async refreshToken() {
      // If there's already a refresh in progress, wait for it
      if (this.refreshPromise) {
        return this.refreshPromise
      }

      // Start new refresh process
      this.refreshPromise = (async () => {
        try {
          if (!this.token) throw new Error('No token to refresh')
          const newToken = await authService.refreshToken(this.token)
          
          // Calculate new expiration based on current remember me status
          const expiresIn = this.isRemembered ? 30 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000
          const expiresAt = Date.now() + expiresIn

          // Preserve remember me status when refreshing
          this.setToken(newToken, expiresAt, this.isRemembered)
        } catch (error) {
          this.logout()
          throw error
        } finally {
          this.refreshPromise = null
        }
      })()

      return this.refreshPromise
    },

    setToken(token: string | null, expiresAt?: number, isRemembered?: boolean) {
      this.token = token
      if (token && expiresAt) {
        setStorageToken({
          token,
          expiresAt,
          isRemembered: isRemembered ?? this.isRemembered
        })
      } else {
        setStorageToken(null)
      }
    },

    clearToken() {
      console.log('🧹 Clearing token')
      this.token = null
      this.isRemembered = false
      this.pendingOtpToken = null
      this.pendingOtpRemembered = false
      // Force clear both storages
      if (process.client) {
        localStorage.removeItem(TOKEN_KEY)
        sessionStorage.removeItem(TOKEN_KEY)
      }
      setStorageToken(null)
      setStoragePendingOtp(null)
    },

    logout() {
      console.log('👋 Logging out')
      this.clearToken()
      this.user = null
      this.error = null
      this.refreshPromise = null
    }
  }
})

// Auto-install the store
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}